(() => {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const relatedState = new WeakMap();
  const observedContainers = new WeakSet();
  const observedAnnouncements = new WeakSet();
  const recommendationCache = new Map();

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const formatMoney = (amount, currency, locale) => {
    try {
      return new Intl.NumberFormat(locale || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format((Number(amount) || 0) / 100);
    } catch (error) {
      return '£' + ((Number(amount) || 0) / 100).toFixed(2);
    }
  };

  const getImageUrl = (product) => {
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && product.featured_image.src) return product.featured_image.src;

    if (Array.isArray(product.images) && product.images.length) {
      const firstImage = product.images[0];
      return typeof firstImage === 'string' ? firstImage : (firstImage && firstImage.src) || '';
    }

    return '';
  };

  const getProductUrl = (product) => ROOT + 'products/' + encodeURIComponent(product.handle);

  const getCartProductIds = (container) => [...new Set(
    (container.dataset.productIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
  )];

  const buildAction = (product, variant, index) => {
    const modalId = 'cevira-related-' + product.id + '-' + index;

    if ((product.variants || []).length === 1) {
      return `
        <product-form>
          <form method="post" action="${ROOT}cart/add" class="cevira-related-product-form">
            <input type="hidden" name="id" value="${variant.id}">
            <input type="hidden" name="quantity" value="1">
            <input type="hidden" name="on_success" value="force_open_drawer">
            <button type="submit" name="add" class="cevira-upsell-add">ADD</button>
          </form>
        </product-form>
      `;
    }

    return `
      <quick-buy-modal
        id="${modalId}"
        handle="${escapeHtml(product.handle)}?variant=${variant.id}"
        class="quick-buy-modal modal modal--lg color-scheme color-scheme--dialog"
      >LOADING</quick-buy-modal>
      <button type="button" aria-controls="${modalId}" class="cevira-upsell-add">ADD</button>
    `;
  };

  const buildCard = (product, currency, locale, index) => {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants.find((item) => item.available) || variants[0];

    if (!variant) return '';

    const image = getImageUrl(product);
    const currentPrice = formatMoney(variant.price, currency, locale);
    const compareAt = Number(variant.compare_at_price || 0);
    const price = Number(variant.price || 0);

    return `
      <div class="cevira-cart-upsell-card">
        <a href="${getProductUrl(product)}" class="cevira-upsell-image" data-instant>
          ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
        </a>

        <div class="cevira-upsell-info">
          <a href="${getProductUrl(product)}" class="cevira-upsell-title" data-instant>
            ${escapeHtml(product.title)}
          </a>

          <div class="cevira-upsell-price-row">
            <span class="cevira-upsell-current-price ${compareAt > price ? 'is-sale' : ''}">${currentPrice}</span>
            ${compareAt > price ? `
              <span class="cevira-upsell-old-price">${formatMoney(compareAt, currency, locale)}</span>
            ` : ''}
          </div>

          <div class="cevira-upsell-action">
            ${buildAction(product, variant, index)}
          </div>
        </div>
      </div>
    `;
  };

  const showFallback = (container) => {
    const section = container.closest('.cevira-related-section');
    const fallback = section?.querySelector('.cevira-related-fallback');

    container.hidden = true;

    if (fallback && fallback.children.length) {
      fallback.hidden = false;
      section?.classList.remove('is-empty');
    } else {
      section?.classList.add('is-empty');
    }
  };

  const showDynamic = (container) => {
    const section = container.closest('.cevira-related-section');
    const fallback = section?.querySelector('.cevira-related-fallback');

    if (fallback) fallback.hidden = true;
    container.hidden = false;
    section?.classList.remove('is-empty');
  };

  const fetchRecommendations = async (productId, signal) => {
    if (recommendationCache.has(productId)) {
      return recommendationCache.get(productId);
    }

    const url = ROOT + 'recommendations/products.json?product_id=' +
      encodeURIComponent(productId) + '&limit=6&intent=related';

    let lastError;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          signal,
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error('Recommendation request failed');

        const data = await response.json();
        const products = Array.isArray(data.products) ? data.products : [];
        recommendationCache.set(productId, products);
        return products;
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        lastError = error;

        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 250));
        }
      }
    }

    throw lastError || new Error('Recommendation request failed');
  };

  const hydrate = async (container) => {
    if (!(container instanceof HTMLElement) || !container.isConnected) return;

    const productIds = getCartProductIds(container);
    const section = container.closest('.cevira-related-section');

    if (!productIds.length) {
      container.replaceChildren();
      showFallback(container);
      return;
    }

    const key = productIds.join(',');
    const previous = relatedState.get(container);

    if (previous?.key === key && (previous.status === 'loading' || previous.status === 'loaded')) {
      return;
    }

    if (previous?.controller) previous.controller.abort();

    const controller = new AbortController();
    relatedState.set(container, { key, status: 'loading', controller });

    const limit = Math.max(1, Math.min(Number(container.dataset.limit || 5), 10));
    const currency = container.dataset.currency || 'GBP';
    const locale = container.dataset.locale || 'en-GB';
    const cartIdSet = new Set(productIds.map(String));

    try {
      const sourceIds = productIds.slice(0, 3);
      const results = await Promise.allSettled(
        sourceIds.map((id) => fetchRecommendations(id, controller.signal))
      );

      if (controller.signal.aborted || !container.isConnected) return;

      const merged = [];
      const seen = new Set();

      for (const result of results) {
        if (result.status !== 'fulfilled') continue;

        for (const product of result.value) {
          const id = String(product.id);

          if (cartIdSet.has(id) || seen.has(id) || !product.available) continue;

          seen.add(id);
          merged.push(product);

          if (merged.length >= limit) break;
        }

        if (merged.length >= limit) break;
      }

      if (controller.signal.aborted || !container.isConnected) return;

      if (!merged.length) {
        container.replaceChildren();
        showFallback(container);
        relatedState.set(container, { key, status: 'loaded', controller: null });
        return;
      }

      container.innerHTML = merged
        .slice(0, limit)
        .map((product, index) => buildCard(product, currency, locale, index))
        .join('');

      showDynamic(container);
      relatedState.set(container, { key, status: 'loaded', controller: null });
    } catch (error) {
      if (error?.name === 'AbortError' || !container.isConnected) return;

      container.replaceChildren();
      showFallback(container);
      relatedState.set(container, { key, status: 'error', controller: null });
    }
  };

  const observeContainer = (container) => {
    if (!(container instanceof HTMLElement) || observedContainers.has(container)) return;

    observedContainers.add(container);
    hydrate(container);

    const observer = new MutationObserver((records) => {
      if (records.some((record) => record.attributeName === 'data-product-ids')) {
        hydrate(container);
      }
    });

    observer.observe(container, {
      attributes: true,
      attributeFilter: ['data-product-ids']
    });
  };

  const initAnnouncement = (rotator) => {
    if (!(rotator instanceof HTMLElement) || observedAnnouncements.has(rotator)) return;

    observedAnnouncements.add(rotator);

    const messages = Array.from(rotator.querySelectorAll('[data-cevira-announcement-message]'));
    if (!messages.length) return;

    messages.forEach((message, index) => {
      message.classList.toggle('is-active', index === 0);
    });

    if (messages.length < 2 || rotator.dataset.autoplay !== 'true') return;

    const speed = Math.max(2, Number(rotator.dataset.speed || 4)) * 1000;
    let index = 0;

    const timer = window.setInterval(() => {
      if (!rotator.isConnected) {
        window.clearInterval(timer);
        return;
      }

      messages[index].classList.remove('is-active');
      index = (index + 1) % messages.length;
      messages[index].classList.add('is-active');
    }, speed);
  };

  const discover = (node) => {
    if (!(node instanceof Element)) return;

    if (node.matches('.cevira-related-products')) observeContainer(node);
    if (node.matches('.cevira-cart-announcement-rotator')) initAnnouncement(node);

    node.querySelectorAll?.('.cevira-related-products').forEach(observeContainer);
    node.querySelectorAll?.('.cevira-cart-announcement-rotator').forEach(initAnnouncement);
  };

  const init = () => {
    document.querySelectorAll('.cevira-related-products').forEach(observeContainer);
    document.querySelectorAll('.cevira-cart-announcement-rotator').forEach(initAnnouncement);

    if (!document.body) return;

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type !== 'childList' || !record.addedNodes.length) continue;

        for (const node of record.addedNodes) {
          discover(node);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();